import type { ConsentRecord } from '../types/search';
import type { AuditLogEntry, PrivacyRequest, SupportTicket } from '../types/platform';

/** Append-only. A change of mind creates a new record that supersedes the old one. */
export const consentRecords: ConsentRecord[] = [
  {
    id: 'consent-1',
    candidateProfileId: 'cand-1',
    type: 'privacy_notice',
    granted: true,
    recordedAt: '2026-06-02T09:40:00Z',
    recordedVia: 'registration',
    ipNote: 'Recorded at registration. In production the request origin is logged server-side.',
  },
  {
    id: 'consent-2',
    candidateProfileId: 'cand-1',
    type: 'general_candidacy',
    granted: true,
    recordedAt: '2026-06-02T09:40:00Z',
    recordedVia: 'registration',
    ipNote: 'Recorded at registration.',
  },
  {
    id: 'consent-3',
    candidateProfileId: 'cand-1',
    type: 'named_employer_presentation',
    granted: true,
    recordedAt: '2026-06-02T09:40:00Z',
    recordedVia: 'registration',
    ipNote: 'Permission to request authorization. Not permission to present.',
  },
  {
    id: 'consent-4',
    candidateProfileId: 'cand-1',
    type: 'confidential_mandate_consideration',
    granted: true,
    recordedAt: '2026-06-02T09:40:00Z',
    recordedVia: 'registration',
    ipNote: 'Recorded at registration.',
  },
  {
    id: 'consent-5',
    candidateProfileId: 'cand-1',
    type: 'marketing_communications',
    granted: false,
    recordedAt: '2026-07-14T20:02:00Z',
    recordedVia: 'privacy_settings',
    ipNote: 'Withdrawn by candidate from privacy settings.',
    supersedesId: 'consent-5-prior',
  },
  {
    id: 'consent-6',
    candidateProfileId: 'cand-1',
    type: 'internal_review',
    granted: true,
    opportunityId: 'opp-1',
    recordedAt: '2026-08-19T14:04:00Z',
    recordedVia: 'application',
    ipNote: 'Recorded during application submission for APP-2026-1841.',
  },
  {
    id: 'consent-auth-1',
    candidateProfileId: 'cand-1',
    type: 'mandate_specific_presentation',
    granted: true,
    mandateId: 'mandate-1',
    opportunityId: 'opp-1',
    employerDisclosedAs: '[Verified client organization name]',
    recordedAt: '2026-08-25T18:32:00Z',
    recordedVia: 'authorization_request',
    ipNote: 'Employer named in the request. Candidate authorized presentation.',
  },
  {
    id: 'consent-auth-2',
    candidateProfileId: 'cand-5',
    type: 'mandate_specific_presentation',
    granted: true,
    mandateId: 'mandate-3',
    opportunityId: 'opp-3',
    employerDisclosedAs: '[Verified client organization name]',
    recordedAt: '2026-08-20T14:10:00Z',
    recordedVia: 'authorization_request',
    ipNote: 'Employer named in the request. Candidate authorized presentation.',
  },
  {
    id: 'consent-auth-4',
    candidateProfileId: 'cand-6',
    type: 'mandate_specific_presentation',
    granted: false,
    mandateId: 'mandate-7',
    opportunityId: 'opp-9',
    employerDisclosedAs: '[Verified client organization name]',
    recordedAt: '2026-08-27T07:45:00Z',
    recordedVia: 'authorization_request',
    ipNote: 'Candidate declined. Application remains open; candidacy unaffected.',
  },
  {
    id: 'consent-7',
    candidateProfileId: 'cand-2',
    type: 'privacy_notice',
    granted: true,
    recordedAt: '2026-08-28T11:44:00Z',
    recordedVia: 'registration',
    ipNote: 'Recorded at registration.',
  },
  {
    id: 'consent-8',
    candidateProfileId: 'cand-2',
    type: 'general_candidacy',
    granted: true,
    recordedAt: '2026-08-28T11:44:00Z',
    recordedVia: 'registration',
    ipNote: 'Recorded at registration.',
  },
  {
    id: 'consent-9',
    candidateProfileId: 'cand-2',
    type: 'confidential_mandate_consideration',
    granted: true,
    recordedAt: '2026-08-28T11:44:00Z',
    recordedVia: 'registration',
    ipNote: 'Recorded at registration.',
  },
  {
    id: 'consent-10',
    candidateProfileId: 'cand-3',
    type: 'privacy_notice',
    granted: true,
    recordedAt: '2026-05-19T14:02:00Z',
    recordedVia: 'registration',
    ipNote: 'Recorded at registration.',
  },
  {
    id: 'consent-11',
    candidateProfileId: 'cand-3',
    type: 'general_candidacy',
    granted: true,
    recordedAt: '2026-05-19T14:02:00Z',
    recordedVia: 'registration',
    ipNote: 'Recorded at registration.',
  },
];

export const auditLog: AuditLogEntry[] = [
  {
    id: 'audit-1',
    action: 'presentation.authorized',
    actorUserId: 'user-cand-1',
    actorName: 'Marion T. Whitfield',
    actorRoleLabel: 'Candidate',
    subjectType: 'CandidatePresentation',
    subjectId: 'pres-1',
    summary: 'Authorized presentation to the named client for mandate MND-2026-041.',
    occurredAt: '2026-08-25T18:32:00Z',
  },
  {
    id: 'audit-2',
    action: 'presentation.sent_to_client',
    actorUserId: 'user-rec-1',
    actorName: 'Claudia B. Nwosu',
    actorRoleLabel: 'Recruiter',
    subjectType: 'CandidatePresentation',
    subjectId: 'pres-1',
    summary: 'Presented candidate profile and written assessment to the client. Authorization consent-auth-1 verified.',
    occurredAt: '2026-08-26T09:00:00Z',
  },
  {
    id: 'audit-3',
    action: 'presentation.declined',
    actorUserId: 'user-cand-6',
    actorName: 'Thomas O. Baptiste',
    actorRoleLabel: 'Candidate',
    subjectType: 'CandidatePresentation',
    subjectId: 'pres-4',
    summary: 'Declined presentation authorization for mandate MND-2026-036. Application remains open.',
    occurredAt: '2026-08-27T07:45:00Z',
  },
  {
    id: 'audit-4',
    action: 'document.reviewed',
    actorUserId: 'user-rec-1',
    actorName: 'Claudia B. Nwosu',
    actorRoleLabel: 'Recruiter',
    subjectType: 'DocumentReview',
    subjectId: 'rev-2',
    summary: 'Revision requested on doc-5 version 1 with three structured feedback items.',
    occurredAt: '2026-08-27T13:15:00Z',
  },
  {
    id: 'audit-5',
    action: 'opportunity.verified',
    actorUserId: 'user-admin-1',
    actorName: 'Adaeze P. Iwu',
    actorRoleLabel: 'Administrator',
    subjectType: 'Opportunity',
    subjectId: 'opp-5',
    summary: 'Verification evidence reviewed and accepted for VEN-2026-0129. Vacancy status confirmed open.',
    occurredAt: '2026-08-26T14:20:00Z',
  },
  {
    id: 'audit-6',
    action: 'employer.verified',
    actorUserId: 'user-admin-1',
    actorName: 'Adaeze P. Iwu',
    actorRoleLabel: 'Administrator',
    subjectType: 'EmployerProfile',
    subjectId: 'emp-1',
    summary: 'Employer account verified. Domain ownership and named-officer authority confirmed.',
    occurredAt: '2026-07-24T11:00:00Z',
  },
  {
    id: 'audit-7',
    action: 'consent.recorded',
    actorUserId: 'user-cand-1',
    actorName: 'Marion T. Whitfield',
    actorRoleLabel: 'Candidate',
    subjectType: 'ConsentRecord',
    subjectId: 'consent-5',
    summary: 'Marketing communications consent withdrawn. Supersedes prior record.',
    occurredAt: '2026-07-14T20:02:00Z',
  },
  {
    id: 'audit-8',
    action: 'application.status_changed',
    actorUserId: 'user-rec-2',
    actorName: 'Idris F. Kaplan',
    actorRoleLabel: 'Recruiter',
    subjectType: 'Application',
    subjectId: 'app-4',
    summary: 'Status changed to Client review following receipt of employer feedback.',
    occurredAt: '2026-08-29T11:05:00Z',
  },
  {
    id: 'audit-9',
    action: 'privacy.request_received',
    actorUserId: 'user-cand-3',
    actorName: 'Priya N. Raghunathan',
    actorRoleLabel: 'Candidate',
    subjectType: 'PrivacyRequest',
    subjectId: 'pr-1',
    summary: 'Data download request received. Reference PRQ-2026-0087 issued.',
    occurredAt: '2026-08-26T09:30:00Z',
  },
  {
    id: 'audit-10',
    action: 'content.published',
    actorUserId: 'user-admin-1',
    actorName: 'Adaeze P. Iwu',
    actorRoleLabel: 'Administrator',
    subjectType: 'ContentPage',
    subjectId: 'page-home',
    summary: 'Home page revision 3 published. Confidential-search section reworded.',
    occurredAt: '2026-08-24T09:12:00Z',
  },
  {
    id: 'audit-11',
    action: 'user.sign_in',
    actorUserId: 'user-admin-1',
    actorName: 'Adaeze P. Iwu',
    actorRoleLabel: 'Administrator',
    subjectType: 'User',
    subjectId: 'user-admin-1',
    summary: 'Successful sign-in with multifactor authentication.',
    occurredAt: '2026-09-01T06:50:00Z',
  },
  {
    id: 'audit-12',
    action: 'notification.sent',
    actorUserId: 'user-rec-1',
    actorName: 'Claudia B. Nwosu',
    actorRoleLabel: 'Recruiter',
    subjectType: 'OutboxEntry',
    subjectId: 'out-4',
    summary: 'Consent request notification sent to candidate. Secure portal link only; no attachment.',
    occurredAt: '2026-08-31T11:01:00Z',
  },
];

export const privacyRequests: PrivacyRequest[] = [
  {
    id: 'pr-1',
    reference: 'PRQ-2026-0087',
    kind: 'data_download',
    requesterUserId: 'user-cand-3',
    requesterEmail: 'candidate.board@velnoris.demo',
    status: 'in_progress',
    submittedAt: '2026-08-26T09:30:00Z',
    dueAt: '2026-09-25T09:30:00Z',
    note: 'Full profile, consent history, application events and document metadata being compiled.',
  },
  {
    id: 'pr-2',
    reference: 'PRQ-2026-0091',
    kind: 'consent_withdrawal',
    requesterUserId: 'user-cand-1',
    requesterEmail: 'candidate@velnoris.demo',
    status: 'completed',
    submittedAt: '2026-07-14T20:02:00Z',
    dueAt: '2026-08-13T20:02:00Z',
    completedAt: '2026-07-14T20:03:00Z',
    note: 'Marketing communications consent withdrawn immediately. Confirmation issued.',
  },
  {
    id: 'pr-3',
    reference: 'PRQ-2026-0094',
    kind: 'correction',
    requesterEmail: '[requester email — identity verification pending]',
    status: 'identity_verification',
    submittedAt: '2026-08-30T13:15:00Z',
    dueAt: '2026-09-29T13:15:00Z',
    note: 'Correction requested to a profile record. Identity verification required before any change is made.',
  },
];

export const supportTickets: SupportTicket[] = [
  {
    id: 'tick-1',
    reference: 'HLP-2026-2204',
    queue: 'employer_search_inquiry',
    name: 'Gregory S. Almeida',
    email: 'employer.pending@velnoris.demo',
    organization: '[Prospective client organization name]',
    subject: 'Chief Merchandising Officer search',
    body:
      'We are considering commissioning a search for a Chief Merchandising Officer and would like to understand your process and fee basis before we proceed internally.',
    status: 'in_progress',
    submittedAt: '2026-08-30T14:58:00Z',
    respondedAt: '2026-08-31T09:20:00Z',
    assignedToUserId: 'user-rec-1',
  },
  {
    id: 'tick-2',
    reference: 'HLP-2026-2211',
    queue: 'candidate_support',
    name: 'Devon R. Achebe',
    email: 'candidate.new@velnoris.demo',
    subject: 'Uploading a document over the size limit',
    body: 'My résumé PDF is larger than the stated limit. What is the best way to submit it?',
    status: 'resolved',
    submittedAt: '2026-08-29T20:40:00Z',
    respondedAt: '2026-08-30T08:05:00Z',
    assignedToUserId: 'user-admin-1',
  },
  {
    id: 'tick-3',
    reference: 'HLP-2026-2218',
    queue: 'technical_support',
    name: 'Rosalind K. Ferreira',
    email: 'candidate.security@velnoris.demo',
    subject: 'Keyboard focus lost in the filter panel',
    body:
      'When I close the filter panel on the opportunities page using the keyboard, focus does not return to the button that opened it.',
    status: 'acknowledged',
    submittedAt: '2026-08-31T15:22:00Z',
    assignedToUserId: 'user-admin-1',
  },
  {
    id: 'tick-4',
    reference: 'HLP-2026-2220',
    queue: 'general_inquiry',
    name: '[Enquirer name]',
    email: '[enquirer email]',
    subject: 'Media enquiry',
    body: '[Enquiry body — placeholder record for demonstration of the intake queue.]',
    status: 'new',
    submittedAt: '2026-09-01T07:15:00Z',
  },
];
